import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChatCircle, WhatsappLogo, InstagramLogo, Clock, Funnel, CurrencyInr, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDuration = (s) => {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${(s/60).toFixed(1)} min`;
  return `${(s/3600).toFixed(1)} hr`;
};

function Tile({ label, value, sub, icon: Icon, tone = "primary", testid }) {
  const colors = {
    primary: "border-[var(--brand-primary)] text-[var(--brand-primary)]",
    success: "border-[var(--status-success)] text-[var(--status-success)]",
    warning: "border-[var(--status-warning)] text-[var(--status-warning)]",
    info:    "border-[var(--status-info)] text-[var(--status-info)]",
  };
  return (
    <div data-testid={testid} className={`bg-[var(--surface,#fff)] border-l-4 ${colors[tone]} rounded shadow-sm p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</div>
        {Icon && <Icon size={18} weight="duotone" className="opacity-70" />}
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

export default function ChannelDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/channel/dashboard").then(r => setData(r.data)).catch(e => setError(e.message));
  }, []);

  if (error) return <div className="p-8 text-[var(--status-danger)]">Failed to load: {error}</div>;
  if (!data) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

  const t = data.totals || {};
  const bc = data.by_channel || {};
  const ls = data.leads_by_status || {};

  return (
    <div className="p-6 space-y-6" data-testid="channel-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Channel Manager</div>
          <h1 className="font-display font-bold text-3xl mt-1 text-[var(--text-primary)]">Conversations & CRM Dashboard</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">Unified WhatsApp + Instagram inbox · auto-leads · response-time SLA</div>
        </div>
        <div className="flex gap-2">
          <Link to="/channel/inbox" className="span-btn" data-testid="link-inbox"><ChatCircle size={14} /> Open Inbox</Link>
          <Link to="/channel/leads" className="span-btn span-btn-ghost" data-testid="link-leads"><Funnel size={14} /> Pipeline</Link>
          <Link to="/channel/flows" className="span-btn span-btn-ghost" data-testid="link-flows"><ArrowRight size={14} /> Flow Builder</Link>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile testid="tile-conversations" label="Total conversations" value={t.conversations || 0} sub={`${t.open || 0} open · ${t.unread || 0} unread`} icon={ChatCircle} />
        <Tile testid="tile-messages"      label="Messages exchanged"  value={t.messages || 0}      sub={`${t.inbound || 0} in · ${t.outbound || 0} out`}    icon={ChatCircle} tone="info" />
        <Tile testid="tile-response"      label="Avg response time"   value={fmtDuration(data.response_time_seconds?.average || 0)} sub={`median ${fmtDuration(data.response_time_seconds?.median || 0)} · ${data.response_time_seconds?.sample_size || 0} samples`} icon={Clock} tone="warning" />
        <Tile testid="tile-pipeline"      label="Open pipeline value" value={fmtINR(data.pipeline_value_inr || 0)} sub={`Won: ${fmtINR(data.won_value_inr || 0)}`} icon={CurrencyInr} tone="success" />
      </div>

      {/* By channel */}
      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
        <h2 className="font-bold text-[var(--text-primary)] mb-3">Activity by channel</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded p-3" data-testid="by-channel-whatsapp">
            <div className="flex items-center gap-2 mb-3">
              <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />
              <span className="font-bold">WhatsApp</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-2xl font-bold">{bc.whatsapp?.conversations || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Conversations</div></div>
              <div><div className="text-2xl font-bold">{bc.whatsapp?.messages || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Messages</div></div>
              <div><div className="text-2xl font-bold">{bc.whatsapp?.leads || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Leads</div></div>
            </div>
          </div>
          <div className="border border-[var(--border)] rounded p-3" data-testid="by-channel-instagram">
            <div className="flex items-center gap-2 mb-3">
              <InstagramLogo size={20} className="text-[#E4405F]" />
              <span className="font-bold">Instagram</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-2xl font-bold">{bc.instagram?.conversations || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Conversations</div></div>
              <div><div className="text-2xl font-bold">{bc.instagram?.messages || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Messages</div></div>
              <div><div className="text-2xl font-bold">{bc.instagram?.leads || 0}</div><div className="text-[10px] text-[var(--text-muted)]">Leads</div></div>
            </div>
            {(bc.instagram?.conversations || 0) === 0 && (
              <div className="text-[10px] text-[var(--status-warning)] mt-2">Instagram integration awaiting Meta Business approval. Schema ready.</div>
            )}
          </div>
        </div>
      </div>

      {/* Leads funnel */}
      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-[var(--text-primary)]">Leads funnel</h2>
          <Link to="/channel/leads" className="text-[11px] text-[var(--brand-primary)] hover:underline">View kanban →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {["new","qualified","proposal","negotiation","won","lost"].map((s, i) => (
            <div key={s} data-testid={`funnel-${s}`} className="text-center p-3 border border-[var(--border)] rounded">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{s}</div>
              <div className={`text-2xl font-bold mt-1 ${s === "won" ? "text-[var(--status-success)]" : s === "lost" ? "text-[var(--status-danger)]" : "text-[var(--text-primary)]"}`}>
                {ls[s] || 0}
              </div>
              {s === "won" && (ls.won || 0) > 0 && <CheckCircle size={14} className="text-[var(--status-success)] mx-auto mt-1" weight="fill" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
